import { ReactNode } from "react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { EmailNavigation } from "./EmailNavigation";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Home, AlertTriangle, LogOut } from "lucide-react";
import { EMAIL_DASHBOARD_VERIFICATION_KEY } from "@/constants/auth";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface EmailPageLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmailPageLayout({
  children,
  title,
  description,
  action,
}: EmailPageLayoutProps) {
  const navigate = useNavigate();

  // Handle logout
  const handleLogout = () => {
    // Clear the verification state
    localStorage.removeItem(EMAIL_DASHBOARD_VERIFICATION_KEY);
    // Redirect to the verification page
    navigate("/admin/email/verify");
  };
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container flex h-16 items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold tracking-tight">
              Numberz Insight
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={handleLogout}
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>
      <EmailNavigation />
      <main className="container py-4 md:py-6 px-4 sm:px-6">
        <div className="mx-auto w-full space-y-6">
          <div className="flex flex-col gap-4">
            <Alert className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 shadow-sm">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
              <AlertTitle className="text-red-800 dark:text-red-300 font-bold text-sm md:text-base">
                AWS SES Production Request Demo Page
              </AlertTitle>
              <AlertDescription className="text-red-700 dark:text-red-400 text-xs md:text-sm">
                This page is designed specifically for the AWS SES team to
                review our production access request. It demonstrates how we use
                AWS SES internally. We are actively building this dashboard to
                be more robust with additional features coming soon to enhance
                our email monitoring capabilities.
              </AlertDescription>
            </Alert>

            <div className="flex justify-center my-6">
              <Link to="/">
                <Button
                  size="lg"
                  className="gap-3 bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-8 h-auto text-lg shadow-lg border-2 border-green-500 hover:border-green-600 rounded-xl transition-all duration-200 hover:scale-105"
                >
                  <Home className="h-6 w-6" />
                  Return to Home Page
                </Button>
              </Link>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                {title}
              </h1>
              {description && (
                <p className="text-gray-700 dark:text-gray-200 text-sm md:text-base">
                  {description}
                </p>
              )}
            </div>
            {action && <div className="flex-shrink-0">{action}</div>}
          </div>
          <div className="rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 p-4 md:p-6 shadow-sm">
            {children}
          </div>
          <div className="text-center text-xs md:text-sm text-gray-500 dark:text-gray-400 py-4">
            <p>
              © {new Date().getFullYear()} Numberz Insight Pty Ltd. All rights
              reserved.
            </p>
            <p className="mt-1">ABN: 55 644 962 167</p>
            <p className="mt-1">
              1300 123 435 | Unit 41/2 Fastline Rd, Truganina VIC 3029
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
